import { onBeforeUnmount, onMounted, type Ref } from 'vue'
import { prefersReducedMotion } from './prefersReducedMotion'

// One-shot confetti on a transparent canvas: a single throw fans pieces upward from an origin,
// then gravity brings them down while they tumble and fade. The simulation
// (createConfettiSim / throwConfetti / stepConfetti / drawConfetti) is pure so it is testable
// without a canvas; useConfetti drives it with requestAnimationFrame until the last piece is gone.

export interface ConfettiPiece {
  x: number
  y: number
  vx: number
  vy: number
  rotation: number
  spin: number
  wobble: number
  width: number
  height: number
  color: string
  life: number
}

export interface ConfettiSim {
  width: number
  height: number
  rng: () => number
  colors: string[]
  pieces: ConfettiPiece[]
}

const GRAVITY = 520
const DRAG_PER_SECOND = 1.1
const LIFETIME_SECONDS = 3.2
export const DEFAULT_COLORS = ['#f5b942', '#ef4d76', '#38d39f', '#b478f5', '#f7d038']

export function createConfettiSim({
  width,
  height,
  rng = Math.random,
  colors = DEFAULT_COLORS,
}: {
  width: number
  height: number
  rng?: () => number
  colors?: string[]
}): ConfettiSim {
  return { width, height, rng, colors, pieces: [] }
}

export function throwConfetti(sim: ConfettiSim, { x, y, count }: { x: number; y: number; count: number }): void {
  const { rng } = sim
  for (let i = 0; i < count; i++) {
    // upward fan between roughly 25° and 155°, so the throw spreads to both sides
    const angle = -Math.PI * (0.15 + 0.7 * rng())
    const speed = 260 + rng() * 380
    sim.pieces.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rotation: rng() * Math.PI * 2,
      spin: (rng() - 0.5) * 14,
      wobble: rng() * Math.PI * 2,
      width: 6 + rng() * 4,
      height: 10 + rng() * 5,
      color: sim.colors[Math.floor(rng() * sim.colors.length)]!,
      life: 1,
    })
  }
}

export function stepConfetti(sim: ConfettiSim, dt: number): void {
  const drag = Math.exp(-DRAG_PER_SECOND * dt)
  sim.pieces = sim.pieces.filter((p) => {
    p.vy += GRAVITY * dt
    p.vx *= drag
    p.vy *= drag
    p.x += p.vx * dt
    p.y += p.vy * dt
    p.rotation += p.spin * dt
    p.wobble += 9 * dt
    p.life -= dt / LIFETIME_SECONDS
    return p.life > 0 && p.y < sim.height + p.height
  })
}

export function drawConfetti(ctx: CanvasRenderingContext2D, sim: ConfettiSim): void {
  ctx.clearRect(0, 0, sim.width, sim.height)
  for (const p of sim.pieces) {
    ctx.save()
    ctx.globalAlpha = Math.min(1, p.life * 3)
    ctx.translate(p.x, p.y)
    ctx.rotate(p.rotation)
    // squashing the height along a cosine reads as the piece flipping over while it falls
    ctx.scale(1, Math.cos(p.wobble))
    ctx.fillStyle = p.color
    ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height)
    ctx.restore()
  }
}

export interface ConfettiOptions {
  originX?: number
  originY?: number
  count?: number
}

export function useConfetti(
  canvas: Ref<HTMLCanvasElement | null>,
  { originX = 0.5, originY = 0.22, count = 140 }: ConfettiOptions = {},
): void {
  let cleanup: (() => void) | null = null

  function start(): void {
    const el = canvas.value
    if (!el || prefersReducedMotion() || typeof window.requestAnimationFrame !== 'function') return
    const ctx = el.getContext('2d')
    if (!ctx) return

    const brand = getComputedStyle(el).getPropertyValue('--brand-default').trim()
    const sim = createConfettiSim({ width: 0, height: 0, colors: brand ? [brand, ...DEFAULT_COLORS] : DEFAULT_COLORS })
    const dpr = window.devicePixelRatio || 1
    const resize = (): void => {
      const { width, height } = el.getBoundingClientRect()
      el.width = Math.round(width * dpr)
      el.height = Math.round(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      sim.width = width
      sim.height = height
    }
    resize()
    // The box can change after mount (the success character finishes loading, the drawer
    // opens): keep the bitmap at the box's size or the drawing gets stretched.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
    observer?.observe(el)
    window.addEventListener('resize', resize)

    let last = performance.now()
    let frame = 0
    let thrown = false
    const stop = (): void => {
      window.cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('resize', resize)
      cleanup = null
    }
    const tick = (now: number): void => {
      const dt = Math.min((now - last) / 1000, 0.05)
      last = now
      if (!thrown) {
        resize()
        throwConfetti(sim, { x: sim.width * originX, y: sim.height * originY, count })
        thrown = true
      }
      stepConfetti(sim, dt)
      drawConfetti(ctx, sim)
      if (sim.pieces.length === 0) {
        ctx.clearRect(0, 0, sim.width, sim.height)
        stop()
        return
      }
      frame = window.requestAnimationFrame(tick)
    }
    cleanup = stop
    frame = window.requestAnimationFrame(tick)
  }

  onMounted(start)
  onBeforeUnmount(() => cleanup?.())
}
